import { bogoLabel } from "../utils/helpers.js";
import { trackAdd } from "../utils/trackCart.js";
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ownVariation } from "../utils/cartIntegrity.js";
import { ModalImageSwiper } from "../components/ModalImageSwiper";
import { ReviewCard } from "../components/ReviewCard";
import { ReviewForm } from "../components/ReviewForm";
import { ProductRecommendations } from "../components/ProductRecommendations";
import { uploadImageToSupabase } from "../utils/imageUpload.js";
import { loadGoogleFont, designThemeStyle, descriptionStyle, dsSetting, storeTextSize } from "../components/DesignStudio/useDesignFonts";
import { Icon } from "../components/Icon.jsx";
import { SoldOutLabel } from "../components/SoldOutLabel.jsx";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";

// ₹ pinned to a system font — the store's chosen font can draw U+20B9 as a peso-looking glyph.
const Rupee = () => <span style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif' }}>₹</span>;

// #rrggbb / #rgb -> rgba(...) — used to derive "soft"/tinted variants of a store's theme
// colour the same way the default theme derives --ink-soft/--border from --ink (same hue,
// lower alpha), so a themed page's muted text and tinted panels stay legible and coherent
// instead of falling back to colours from a completely different palette.
const hexToRgba = (hex, alpha) => {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return `rgba(0,0,0,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

// The route re-uses this component when only the product id changes (e.g. tapping a recommended product), which kept the
// previous product's selected option / quantity / answers in state — and added them to the NEW product's bag line. Keying
// on the product id gives every product a fresh instance, so nothing can carry over.
export function ProductPage(props) {
  const { productId } = useParams();
  return <ProductPageInner key={productId} {...props} />;
}

function ProductPageInner({ cart, setCart, customer, session, onCart }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  // If this product's store has a published Design Studio theme, the whole product page
  // wears it — background, fonts, text colour, card radius — no matter how the shopper
  // arrived (marketplace, direct link, etc.). null = store has no custom design → default look.
  const [storeDesign, setStoreDesign] = useState(null);

  const [product,           setProduct]           = useState(null);
  const [reviews,           setReviews]           = useState([]);
  const [avgRating,         setAvgRating]         = useState(0);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [qty,               setQty]               = useState(1);
  const [loading,           setLoading]           = useState(true);
  const [justAdded,         setJustAdded]         = useState(false);
  const [customAnswer,      setCustomAnswer]      = useState("");
  const [customImage,       setCustomImage]       = useState("");
  const [imgUploading,      setImgUploading]      = useState(false);

  // QNA state
  const [qas,          setQas]          = useState([]);
  const [qaOffByServer, setQaOffByServer] = useState(false);   // the Q&A fetch told us this store switched Q&A off
  const [question,     setQuestion]     = useState("");
  const [submittingQ,  setSubmittingQ]  = useState(false);
  const [replyTexts,   setReplyTexts]   = useState({});   // { [questionId]: text }
  const [submittingA,  setSubmittingA]  = useState({});   // { [questionId]: bool }
  const [qaError,      setQaError]      = useState("");

  // Fetch product details
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/marketplace/product/${productId}`);
        if (res.ok) {
          const data = await res.json();
          setProduct(data);
          // Intentionally do NOT auto-select a variation: the page opens on the
          // product's primary image, and tapping a variation swaps it in (tapping the
          // same one again clears it back to primary).
        }
      } catch (e) {
        console.error("Error fetching product:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // The store's PUBLISHED Design Studio theme (if any) now arrives INLINE on the product
  // response itself (`product.storeDesign`), not from a second fetch kicked off only once
  // product.user_id was known. That old two-round-trip chain (product, then — only after
  // learning the store id — theme) was the actual cause of a themed product page flashing the
  // default Bloom look for ~2s before the real theme swapped in: the page necessarily rendered
  // once with storeDesign still null before the second fetch could even start. One response now
  // carries both, so the theme is known at the exact same moment the product is. Still
  // authoritative on the server — a lapsed PRO store's response simply omits storeDesign, so
  // the page falls back to the default look exactly as before.
  useEffect(() => {
    const d = product?.storeDesign;
    if (d?.design) {
      setStoreDesign(d.design);
      [d.design.body_font, d.design.header_font, dsSetting(d.design, "desc_font")].filter(Boolean).forEach(loadGoogleFont);
    } else {
      setStoreDesign(null);
    }
  }, [product]);

  // Fetch reviews + QAs in parallel
  useEffect(() => {
    if (!productId) return;
    const fetchReviews = async () => {
      try {
        const [reviewsRes, avgRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/reviews/product/${productId}`),
          fetch(`${BACKEND_URL}/api/reviews/avg/${productId}`),
        ]);
        if (reviewsRes.ok) setReviews(await reviewsRes.json().then(d => Array.isArray(d) ? d : []));
        if (avgRes.ok)     setAvgRating((await avgRes.json()).average_rating || 0);
      } catch (e) { console.error("Error fetching reviews:", e); }
    };
    const fetchQAs = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/qa/product/${productId}`);
        if (res.headers.get("X-QA-Enabled") === "0") setQaOffByServer(true);
        if (res.ok) setQas(await res.json().then(d => Array.isArray(d) ? d : []));
      } catch (e) { console.error("Error fetching QAs:", e); }
    };
    fetchReviews();
    fetchQAs();
  }, [productId]);

  // Is the logged-in seller the owner of this product?
  const isOwner = !!session?.userId && !!product && session.userId === product.user_id;
  // Seller switched Questions & Answers off in Settings (flag from the store info, or the Q&A fetch itself).
  const qaHidden = product?._storeInfo?.qa_enabled === false || qaOffByServer;

  const handleAddCart = () => {
    if (!product) return;
    if (product._storeInfo?.checkout_paused || product.checkout_paused) {
      alert("This store has paused orders right now. Please check back soon!");
      return;
    }
    // Require custom answer if the seller has set a question
    if (product.custom_question && !customAnswer.trim()) {
      alert("Please answer the store's question before adding to cart.");
      return;
    }
    // Require uploaded image if the seller has enabled it
    if (product.require_upload && !customImage) {
      alert("Please upload the required image before adding to cart.");
      return;
    }
    // The option on the bag line is resolved from THIS product's own list, never taken from state as-is: a selection that
    // isn't one of this product's options (a leftover from another product) resolves to nothing instead of being priced.
    const chosen = ownVariation(product, selectedVariation);
    // A variation product needs a variation chosen (no longer auto-selected)
    if (product.variations && product.variations.length > 0 && !chosen) {
      alert("Please select a variation before adding to cart.");
      return;
    }
    // null/undefined stock = no tracking → cap at 99; 0 = truly out of stock
    const maxStock = stock != null ? Number(stock) : 99;
    const addQty = Math.min(qty, maxStock);
    if (addQty <= 0) return;
    const item = {
      id: product.id, name: product.name,
      price: chosen?.price || product.price,
      img: product.img, images: product.images,
      qty: addQty, selectedVariation: chosen,
      free_shipping: !!product.free_shipping,
      _storeInfo: product._storeInfo, user_id: product.user_id,
      ...(product.custom_question ? { custom_question: product.custom_question, custom_answer: customAnswer.trim() } : {}),
      ...(product.require_upload && customImage ? { custom_image: customImage } : {}),
    };
    const newCart = [...cart];
    const key = `${product.id}__${chosen?.name || ""}`;
    const existing = newCart.find(c => `${c.id}__${c.selectedVariation?.name || ""}` === key);
    if (existing) existing.qty = Math.min(existing.qty + addQty, maxStock);
    else newCart.push(item);
    setCart(newCart);
    trackAdd(product, { qty: addQty, variation: chosen?.name, customer });   // seller's "Left in carts" panel
    onCart?.();
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1800);
  };

  const handleBuy = () => {
    // Guest checkout — no login required; the cart's address modal collects buyer details.
    handleAddCart();
    navigate("/cart");
  };

  // ── QNA handlers ─────────────────────────────────────────────────────────────
  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    if (!customer) {
      localStorage.setItem("bloom_redirect", window.location.pathname);
      navigate("/customer/login");
      return;
    }
    setSubmittingQ(true);
    setQaError("");
    try {
      const id = `qa_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const res = await fetch(`${BACKEND_URL}/api/qa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, product_id: productId, customer_id: customer.id, question_text: question.trim() }),
      });
      if (res.ok) {
        const newQ = await res.json();
        setQas(prev => [{ ...newQ, answers: [] }, ...prev]);
        setQuestion("");
      } else {
        // 403 = the store switched Q&A off, or blocked this person — say so instead of "try again".
        const d = res.status === 403 ? await res.json().catch(() => ({})) : {};
        setQaError(d.error || "Failed to post question. Try again.");
      }
    } catch (e) {
      setQaError("Failed to post question. Try again.");
    } finally {
      setSubmittingQ(false);
    }
  };

  const handleAnswer = async (questionId) => {
    const text = (replyTexts[questionId] || "").trim();
    if (!text || !isOwner) return;
    setSubmittingA(prev => ({ ...prev, [questionId]: true }));
    try {
      const _stok = (()=>{ try { return JSON.parse(localStorage.getItem("bloom:sess")||"null")?.token || ""; } catch { return ""; } })();
      const res = await fetch(`${BACKEND_URL}/api/qa/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-store-id": session.userId, ...(_stok ? { Authorization: `Bearer ${_stok}` } : {}) },
        body: JSON.stringify({ question_id: questionId, answer_text: text, store_id: session.userId, is_store_owner: true }),
      });
      if (res.ok) {
        const newAns = await res.json();
        setQas(prev => prev.map(q => q.id === questionId ? { ...q, answers: [...(q.answers||[]), newAns] } : q));
        setReplyTexts(prev => ({ ...prev, [questionId]: "" }));
      }
    } catch (e) {
      console.error("Error posting answer:", e);
    } finally {
      setSubmittingA(prev => ({ ...prev, [questionId]: false }));
    }
  };

  // ── Loading / not found ────────────────────────────────────────────────────
  // Amazon-style hero swap: selecting a variation that has its own image shows it on
  // top; variations without one fall back to the product's primary image.
  const galleryImages = useMemo(() => {
    const base = (product?.images && product.images.length > 0) ? product.images : (product?.img ? [product.img] : []);
    const vimg = selectedVariation && product?.variation_images?.[selectedVariation.name];
    if (vimg) return [vimg, ...base.filter(u => u !== vimg)];
    return base;
  }, [product, selectedVariation?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div style={{ padding:"100px 20px", textAlign:"center", color:"var(--ink-soft)" }}>Loading...</div>
  );
  if (!product) return (
    <div style={{ padding:"100px 20px", textAlign:"center", color:"var(--ink-soft)" }}>Product not found</div>
  );

  const price    = selectedVariation?.price || product.price;
  const mrp      = selectedVariation?.mrp   || product.mrp;
  const stock    = selectedVariation?.stock ?? product.stock;
  const discount = mrp && price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const isMobile = window.innerWidth < 768;

  // sold = store manually marked as sold (thrift / one-of-a-kind)
  // outOfStock = stock counter hit 0
  const maxStock   = stock != null ? Number(stock) : 99;  // null = no tracking → 99; 0 = truly out
  const isSold     = !!product.sold || product.is_live === false;
  const outOfStock = isSold || maxStock === 0;

  // Store's Design Studio theme (background + fonts + text colour + card radius), or the
  // default look when the store has no published design.
  const themeStyle = designThemeStyle(storeDesign);
  // The store's "Text size" (Design Studio) scales every text size on this page. Unset → exactly the px written here.
  const fs = (n) => storeTextSize(storeDesign, n);

  // Theme-aware colour tokens. Bug fixed here: the page background/font DID already follow
  // the store's theme, but every individual piece of UI (buttons, price, ratings, borders)
  // was hardcoded to Bloom's default --ink/--teal/--pink-btn regardless — so a themed store
  // with a dark background got dark default text on top of it (unreadable) and generic teal
  // buttons that clashed with the store's own colours. With no published design these are
  // BYTE-IDENTICAL to the previous hardcoded values, so nothing changes for any other store.
  const hasTheme     = !!storeDesign;
  const inkColor     = hasTheme ? (storeDesign.text_color || "#111111") : "var(--ink)";
  const inkSoftColor = hasTheme ? hexToRgba(storeDesign.text_color || "#111111", 0.62) : "var(--ink-soft)";
  const borderColor  = hasTheme ? hexToRgba(storeDesign.text_color || "#111111", 0.16) : "var(--border)";
  const accentColor  = hasTheme ? (storeDesign.accent_color || storeDesign.secondary_color || "#FF6987") : "var(--pink-btn)";
  const accentSoftBg = hasTheme ? hexToRgba(accentColor, 0.1) : "rgba(47,99,221,0.08)";
  // The Q&A section's teal tint (and the "Post Question"/"Answer" buttons, which had no real
  // background at all — className="btn-teal" matches no CSS rule anywhere in the codebase,
  // so they rendered as unstyled default browser buttons on every store, themed or not).
  const brandColor    = hasTheme ? accentColor : "#2F63DD";
  const brandSoftBg   = hasTheme ? hexToRgba(accentColor, 0.08) : "rgba(47,99,221,0.06)";
  const brandSoftBorder = hasTheme ? hexToRgba(accentColor, 0.2) : "rgba(47,99,221,0.2)";

  return (
    <div className={storeDesign?.body_bold ? "ds-store--bold" : undefined} style={{ paddingTop:"60px", minHeight:"100vh", ...(themeStyle || { background:"var(--bg)" }) }}>
      <div style={{ maxWidth:"1200px", margin:"0 auto", padding:"20px" }}>

        {/* Back button */}
        <button onClick={() => navigate(-1)}
          style={{ background:"none", border:"none", color:inkSoftColor, cursor:"pointer", fontSize:fs(14), marginBottom:"20px", padding:"8px 0", display:"flex", alignItems:"center", gap:"4px" }}>
          ← Back
        </button>

        {/* ── Main product section ── */}
        <div style={{ display:"flex", gap:"40px", marginBottom:"40px", flexDirection: isMobile ? "column" : "row" }}>

          {/* Image gallery */}
          <div style={{ flex:1, minWidth:"300px", position:"relative" }}>
            <ModalImageSwiper images={galleryImages} />
            {/* Offer ribbon over the gallery, matching the product cards */}
            {product.bogo_eligible && bogoLabel(product._storeInfo) && (
              <div style={{ position:"absolute", top:10, right:10, zIndex:5, background:"var(--warn-bg)", color:"var(--warn-fg)", border:"1px solid var(--warn-bd)", fontSize:fs(11), fontWeight:800, padding:"6px 12px", borderRadius:4, pointerEvents:"none" }}>
                {bogoLabel(product._storeInfo)}
              </div>
            )}
          </div>

          {/* Product details */}
          <div style={{ flex:1 }}>
            <h1 style={{ fontSize:fs(22), fontWeight:600, lineHeight:1.3, color:inkColor, margin:"0 0 12px" }}>
              {product.name}
            </h1>

            {/* Store info */}
            {product._storeInfo && (
              <div onClick={() => navigate(`/store/${product._storeInfo.store_slug}`)}
                style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"16px", padding:"10px 12px", background: hasTheme ? hexToRgba(inkColor, 0.06) : "rgba(0,0,0,0.04)", borderRadius:"4px", cursor:"pointer" }}>
                {product._storeInfo.profile_image && (
                  <img src={product._storeInfo.profile_image} style={{ width:"24px", height:"24px", borderRadius:"50%", objectFit:"cover" }} alt=""/>
                )}
                <span style={{ fontSize:fs(13), fontWeight:600, color:inkColor }}>
                  <Icon n="store" size="1.1em" inline /> {product._storeInfo.store_name}
                </span>
                <span style={{ fontSize:fs(12), color:inkSoftColor, marginLeft:"auto" }}>Visit store →</span>
              </div>
            )}

            {/* Rating */}
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"16px" }}>
              <div style={{ display:"flex", gap:"2px" }}>
                {[1,2,3,4,5].map(i => (
                  <span key={i} style={{ fontSize:fs(16), opacity: i <= Math.round(avgRating) ? 1 : 0.3 }}>★</span>
                ))}
              </div>
              <span style={{ fontSize:fs(13), color:inkSoftColor }}>
                {avgRating.toFixed(1)} ({reviews.length} {reviews.length === 1 ? "review" : "reviews"})
              </span>
            </div>

            {/* Price */}
            <div style={{ marginBottom:"20px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                <span style={{ fontSize:fs(22), fontWeight:600, color:inkColor }}><Rupee/>{price}</span>
                {mrp && <span style={{ fontSize:fs(14), color:inkSoftColor, textDecoration:"line-through" }}><Rupee/>{mrp}</span>}
                {discount > 0 && <span style={{ fontSize:fs(12), fontWeight:700, color:accentColor, background:accentSoftBg, padding:"2px 6px", borderRadius:"4px" }}>{discount}% off</span>}
              </div>
              {product.free_shipping && <div style={{ fontSize:fs(13), color:"var(--green)", fontWeight:700, marginTop:"6px" }}><Icon n="truck" size="1.1em" inline /> Free shipping</div>}
              {product.bogo_eligible && bogoLabel(product._storeInfo) && (
                <div style={{ display:"inline-block", fontSize:fs(13), color:"var(--warn-fg)", background:"var(--warn-bg)", border:"1px solid var(--warn-bd)", borderRadius:4, padding:"3px 10px", fontWeight:800, marginTop:"8px" }}><Icon n="bag" size="1.1em" inline /> {bogoLabel(product._storeInfo)}</div>
              )}
            </div>

            {/* Variation selector */}
            {product.variations && product.variations.length > 0 && (
              <div style={{ marginBottom:"20px" }}>
                <label style={{ display:"block", fontSize:fs(13), fontWeight:600, color:inkColor, marginBottom:"8px" }}>Choose variation:</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
                  {product.variations.map(v => {
                    const vSoldOut = Number(v.stock||0) === 0;
                    const active = selectedVariation?.name === v.name;
                    return (
                      <button key={v.name} type="button"
                        onClick={() => { if (vSoldOut) return; setSelectedVariation(cur => cur?.name === v.name ? null : v); setQty(1); }}
                        style={{
                          padding:"8px 14px", borderRadius:"4px", cursor: vSoldOut ? "not-allowed" : "pointer",
                          border:`1.5px solid ${active ? accentColor : "var(--border-mid)"}`,
                          background: active ? accentColor : vSoldOut ? "rgba(0,0,0,0.04)" : "var(--surface)",
                          color: active ? "#fff" : vSoldOut ? inkSoftColor : inkColor,
                          fontSize:fs(13), fontWeight:600, lineHeight:1.3, textAlign:"left",
                          opacity: vSoldOut ? 0.6 : 1,
                        }}>
                        {v.name}
                        <span style={{ display:"block", fontSize:fs(12), fontWeight:700, opacity:0.9 }}>₹{(Number(v.price)||0).toLocaleString()}</span>
                        {vSoldOut && <span style={{ display:"block", fontSize:fs(10), color:"var(--red)" }}>Sold out</span>}
                      </button>
                    );
                  })}
                </div>
                {!selectedVariation && <p style={{ fontSize:fs(12), color:inkSoftColor, marginTop:"8px" }}>Tap a variation to select it — tap again to go back to the main image.</p>}
              </div>
            )}

            {/* Quantity */}
            <div style={{ marginBottom:"20px" }}>
              <label style={{ display:"block", fontSize:fs(13), fontWeight:600, color:inkColor, marginBottom:"8px" }}>Quantity:</label>
              <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
                <button onClick={() => setQty(Math.max(1, qty - 1))}
                  style={{ width:"40px", height:"40px", border:`1px solid ${borderColor}`, borderRadius:"4px", cursor:"pointer", fontSize:fs(18), fontWeight:600, background:"transparent", color:inkColor }}>−</button>
                <span style={{ minWidth:"32px", textAlign:"center", fontSize:fs(16), fontWeight:700, color:inkColor }}>{qty}</span>
                <button onClick={() => setQty(q => Math.min(maxStock, q + 1))}
                  disabled={outOfStock || qty >= maxStock}
                  style={{ width:"40px", height:"40px", border:`1px solid ${borderColor}`, borderRadius:"4px", cursor: (outOfStock || qty >= maxStock) ? "not-allowed" : "pointer", fontSize:fs(18), fontWeight:600, background:"transparent", color:inkColor, opacity: (outOfStock || qty >= maxStock) ? 0.4 : 1 }}>+</button>
                {!outOfStock && stock != null && <span style={{ fontSize:fs(12), color:inkSoftColor, marginLeft:4 }}>{stock} in stock</span>}
              </div>
            </div>

            {/* Custom question from seller */}
            {product?.custom_question && !outOfStock && (
              <div style={{ marginBottom:"20px", background:"var(--warn-bg)", borderRadius:"4px", padding:"16px", border:"1.5px solid var(--warn-bd)" }}>
                <label style={{ display:"block", fontSize:fs(13), fontWeight:700, color:"var(--warn-fg)", marginBottom:"8px", lineHeight:1.4 }}>
                  <Icon n="chat" size="1.1em" inline /> {product.custom_question} <span style={{ color:"var(--red)", marginLeft:2 }}>*</span>
                </label>
                <textarea
                  value={customAnswer}
                  onChange={e => setCustomAnswer(e.target.value.slice(0, 500))}
                  placeholder="Type your answer here…"
                  rows={3}
                  style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${customAnswer.trim()?"rgba(245,158,11,0.5)":"var(--border)"}`, borderRadius:"4px", fontSize:fs(13), fontFamily:"inherit", resize:"vertical", boxSizing:"border-box", background:"var(--white)", color:"var(--ink)", lineHeight:1.5, outline:"none", transition:"border-color 0.2s" }}
                />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                  <span style={{ fontSize:fs(11), color:"var(--warn-fg)", fontWeight:600 }}>Required — the seller needs this to fulfil your order</span>
                  <span style={{ fontSize:fs(11), color:"var(--ink-soft)" }}>{customAnswer.length}/500</span>
                </div>
              </div>
            )}

            {/* Required image upload from customer */}
            {product?.require_upload && !outOfStock && (
              <div style={{ marginBottom:"20px", background:"var(--info-bg)", borderRadius:"4px", padding:"16px", border:"1.5px solid var(--info-bd)" }}>
                <label style={{ display:"block", fontSize:fs(13), fontWeight:700, color:"var(--info-fg)", marginBottom:"8px", lineHeight:1.4 }}>
                  <Icon n="camera" size="1.1em" inline /> Upload an image for this order <span style={{ color:"var(--red)", marginLeft:2 }}>*</span>
                </label>
                {customImage ? (
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <img src={customImage} alt="Your upload" style={{ width:72, height:72, objectFit:"cover", borderRadius:4, border:"1.5px solid var(--info-bd)" }}/>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      <span style={{ fontSize:fs(12), color:"var(--info-fg)", fontWeight:700 }}><Icon n="done" size="1.1em" inline /> Image uploaded</span>
                      <button onClick={() => setCustomImage("")} style={{ background:"none", border:"1.5px solid var(--border)", borderRadius:4, padding:"5px 12px", fontSize:fs(12), cursor:"pointer", color:"var(--ink-soft)" }}>Remove & re-upload</button>
                    </div>
                  </div>
                ) : (
                  <label style={{ display:"block", border:"2px dashed rgba(59,130,246,0.35)", borderRadius:4, padding:"18px 12px", textAlign:"center", cursor: imgUploading ? "wait" : "pointer", background:"var(--white)" }}>
                    <input type="file" accept="image/*" disabled={imgUploading} style={{ display:"none" }}
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        setImgUploading(true);
                        try {
                          const url = await uploadImageToSupabase(file, `customer-uploads/${product?.user_id || "misc"}`);
                          setCustomImage(url);
                        } catch(err) {
                          alert("Image upload failed: " + (err?.message || err) + "\nPlease try again.");
                        }
                        setImgUploading(false);
                      }}/>
                    <span style={{ fontSize:fs(13), fontWeight:700, color: imgUploading ? "var(--ink-soft)" : "#1D4ED8" }}>{imgUploading ? "Uploading…" : "Tap to choose an image"}</span>
                    <p style={{ fontSize:fs(11), color:"var(--ink-soft)", marginTop:4 }}>The seller needs this image to fulfil your order</p>
                  </label>
                )}
              </div>
            )}

            {/* Action buttons */}
            {outOfStock ? (
              <SoldOutLabel block height={48} fontSize={15} />
            ) : product._storeInfo?.checkout_paused ? (
              <div style={{ padding:"14px 20px", background:"var(--surface)", borderRadius:"4px", textAlign:"center", fontSize:fs(14), fontWeight:700, color:"var(--ink-soft)" }}>
                <Icon n="locked" size="1.1em" inline /> This store has paused orders right now
              </div>
            ) : (
              (() => {
                const key = `${product.id}__${selectedVariation?.name || ""}`;
                const cartQty = cart.find(c => `${c.id}__${c.selectedVariation?.name || ""}` === key)?.qty || 0;
                const needsAnswer = (!!product.custom_question && !customAnswer.trim()) || (!!product.require_upload && !customImage);
                return (
                  <div style={{ display:"flex", gap:"12px" }}>
                    <button onClick={handleAddCart} disabled={needsAnswer}
                      style={{
                        flex:1, padding:"14px", borderRadius:"4px", cursor: needsAnswer ? "not-allowed" : "pointer", fontSize:fs(14), fontWeight:700,
                        transition:"all 0.2s", opacity: needsAnswer ? 0.45 : 1,
                        background: justAdded ? accentColor : "transparent",
                        border: `2px solid ${accentColor}`,
                        color: justAdded ? "#fff" : accentColor,
                      }}>
                      {justAdded
                        ? `✓ Added to Cart${cartQty > 0 ? ` ×${cartQty}` : ""}`
                        : cartQty > 0
                          ? `+ Add to Cart ×${cartQty}`
                          : "+ Add to Cart"
                      }
                    </button>
                    <button onClick={handleBuy} disabled={needsAnswer} className={hasTheme ? undefined : "btn-pink"}
                      style={{ flex:1, padding:"14px", fontSize:fs(14), fontWeight:700, opacity: needsAnswer ? 0.45 : 1, cursor: needsAnswer ? "not-allowed" : undefined,
                        ...(hasTheme ? { background:accentColor, color:"#fff", border:"none", borderRadius:"4px" } : {}) }}>
                      <Icon n="instant" size="1.1em" inline /> Buy Now
                    </button>
                  </div>
                );
              })()
            )}

            {/* Description */}
            {product.description && (
              <div style={{ marginTop:"24px", paddingTop:"24px", borderTop:`1px solid ${borderColor}` }}>
                <h3 style={{ fontSize:fs(14), fontWeight:700, color:inkColor, marginBottom:"8px" }}>Description</h3>
                <p style={{ fontSize:fs(13), color:inkSoftColor, lineHeight:"1.6", whiteSpace:"pre-wrap", ...descriptionStyle(storeDesign) }}>{product.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Reviews section ── */}
        <div style={{ marginBottom:"40px", paddingTop:"24px", borderTop:`1px solid ${borderColor}` }}>
          <h2 style={{ fontSize:fs(18), fontWeight:700, color:inkColor, marginBottom:"20px" }}>Reviews & Ratings</h2>

          {/* Rating summary */}
          <div style={{ marginBottom:"24px", padding:"20px", background:accentSoftBg, borderRadius:"4px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"20px" }}>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:fs(34), fontWeight:700, color:inkColor }}>{avgRating.toFixed(1)}</div>
                <div style={{ display:"flex", gap:"2px", justifyContent:"center", marginTop:"4px" }}>
                  {[1,2,3,4,5].map(i => (
                    <span key={i} style={{ fontSize:fs(14), opacity: i <= Math.round(avgRating) ? 1 : 0.3 }}>★</span>
                  ))}
                </div>
                <div style={{ fontSize:fs(12), color:inkSoftColor, marginTop:"4px" }}>{reviews.length} reviews</div>
              </div>
              <div style={{ flex:1 }}>
                {[5,4,3,2,1].map(star => {
                  const count = reviews.filter(r => r.rating === star).length;
                  const pct   = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                  return (
                    <div key={star} style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
                      <span style={{ fontSize:fs(12), color:inkSoftColor, width:"20px" }}>{star}★</span>
                      <div style={{ flex:1, height:"6px", background:borderColor, borderRadius:"3px", overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:accentColor }}/>
                      </div>
                      <span style={{ fontSize:fs(12), color:inkSoftColor, width:"30px", textAlign:"right" }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Review form (purchase required) */}
          {customer && <ReviewForm productId={productId} customerId={customer.id} storeId={product?.user_id || ""} onReviewAdded={() => window.location.reload()}/>}

          {/* Reviews list */}
          <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
            {reviews.length === 0
              ? <div style={{ padding:"20px", textAlign:"center", color:inkSoftColor, fontSize:fs(14) }}>Be the first to review this product! <Icon n="star" size="1.1em" inline /></div>
              : reviews.map(review => <ReviewCard key={review.id} review={review}/>)
            }
          </div>
        </div>

        {/* ── Q&A section ── */}
        {!qaHidden && (
        <>
        <div style={{ marginBottom:"40px", paddingTop:"24px", borderTop:`1px solid ${borderColor}` }}>
          <h2 style={{ fontSize:fs(18), fontWeight:700, color:inkColor, marginBottom:"6px" }}>Questions & Answers</h2>
          <p style={{ fontSize:fs(13), color:inkSoftColor, marginBottom:"24px" }}>
            Ask the store owner anything about this product.
          </p>

          {/* Ask a question */}
          <div style={{ marginBottom:"32px", padding:"18px", background:brandSoftBg, border:`1px solid ${brandSoftBorder}`, borderRadius:"4px" }}>
            <label style={{ display:"block", fontSize:fs(13), fontWeight:700, color:inkColor, marginBottom:"10px" }}>
              Ask a question
            </label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder={customer ? "What would you like to know about this product?" : "Sign in to ask a question"}
              disabled={!customer}
              rows={3}
              style={{ width:"100%", padding:"10px 12px", border:`1px solid ${borderColor}`, borderRadius:"4px", fontSize:fs(13), color:inkColor, background: customer ? "var(--bg)" : "rgba(0,0,0,0.03)", resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }}
            />
            {qaError && <p style={{ fontSize:fs(12), color:"var(--red)", margin:"6px 0 0" }}>{qaError}</p>}
            <div style={{ display:"flex", justifyContent:"flex-end", marginTop:"10px" }}>
              {customer
                ? <button onClick={handleAskQuestion} disabled={submittingQ || !question.trim()}
                    style={{ padding:"8px 20px", fontSize:fs(13), fontWeight:700, background:brandColor, color:"#fff", border:"none", borderRadius:"4px", opacity: submittingQ || !question.trim() ? 0.5 : 1, cursor: submittingQ || !question.trim() ? "not-allowed" : "pointer" }}>
                    {submittingQ ? "Posting…" : "Post Question"}
                  </button>
                : <button onClick={() => { localStorage.setItem("bloom_redirect", window.location.pathname); navigate("/customer/login"); }}
                    style={{ padding:"8px 20px", fontSize:fs(13), fontWeight:700, background:brandColor, color:"#fff", border:"none", borderRadius:"4px" }}>
                    Sign in to ask
                  </button>
              }
            </div>
          </div>

          {/* Q&A list */}
          {qas.length === 0
            ? <div style={{ padding:"20px", textAlign:"center", color:inkSoftColor, fontSize:fs(14) }}>No questions yet — be the first to ask!</div>
            : qas.map(qa => (
              <div key={qa.id} style={{ marginBottom:"24px", borderBottom:`1px solid ${borderColor}`, paddingBottom:"24px" }}>

                {/* Question */}
                <div style={{ display:"flex", gap:"10px", marginBottom:"12px" }}>
                  <span style={{ fontSize:fs(18), flexShrink:0 }}><Icon n="chat" size="1.1em" inline /></span>
                  <div style={{ flex:1 }}>
                    <p style={{ margin:0, fontSize:fs(14), color:inkColor, fontWeight:500, lineHeight:1.5 }}>{qa.question_text}</p>
                    <span style={{ fontSize:fs(11), color:inkSoftColor }}>
                      {qa.created_at ? new Date(qa.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : ""}
                    </span>
                  </div>
                </div>

                {/* Answers */}
                {(qa.answers || []).length > 0 && (
                  <div style={{ marginLeft:"28px", display:"flex", flexDirection:"column", gap:"10px" }}>
                    {qa.answers.map(ans => (
                      <div key={ans.id} style={{ display:"flex", gap:"10px", padding:"12px", background:brandSoftBg, borderRadius:"4px", border:`1px solid ${hasTheme ? hexToRgba(accentColor, 0.15) : "rgba(47,99,221,0.15)"}` }}>
                        <span style={{ fontSize:fs(16), flexShrink:0 }}><Icon n="store" size="1.1em" inline /></span>
                        <div style={{ flex:1 }}>
                          <p style={{ margin:"0 0 4px", fontSize:fs(13), color:inkColor, lineHeight:1.5 }}>{ans.answer_text}</p>
                          <span style={{ fontSize:fs(11), color:brandColor, fontWeight:600 }}>Store Owner</span>
                          {ans.created_at && (
                            <span style={{ fontSize:fs(11), color:inkSoftColor, marginLeft:8 }}>
                              · {new Date(ans.created_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Owner reply input */}
                {isOwner && !(qa.answers || []).length && (
                  <div style={{ marginLeft:"28px", marginTop:"10px", display:"flex", gap:"8px" }}>
                    <input
                      type="text"
                      value={replyTexts[qa.id] || ""}
                      onChange={e => setReplyTexts(prev => ({ ...prev, [qa.id]: e.target.value }))}
                      placeholder="Answer this question…"
                      style={{ flex:1, padding:"8px 12px", border:`1px solid ${borderColor}`, borderRadius:"4px", fontSize:fs(13), color:inkColor }}
                      onKeyDown={e => e.key === "Enter" && handleAnswer(qa.id)}
                    />
                    <button onClick={() => handleAnswer(qa.id)} disabled={submittingA[qa.id] || !(replyTexts[qa.id] || "").trim()}
                      style={{ padding:"8px 16px", fontSize:fs(13), fontWeight:700, background:brandColor, color:"#fff", border:"none", borderRadius:"4px", opacity: submittingA[qa.id] ? 0.5 : 1 }}>
                      {submittingA[qa.id] ? "…" : "Answer"}
                    </button>
                  </div>
                )}

                {/* Owner can also add a second answer if they want to clarify */}
                {isOwner && (qa.answers || []).length > 0 && (
                  <div style={{ marginLeft:"28px", marginTop:"8px", display:"flex", gap:"8px" }}>
                    <input
                      type="text"
                      value={replyTexts[qa.id] || ""}
                      onChange={e => setReplyTexts(prev => ({ ...prev, [qa.id]: e.target.value }))}
                      placeholder="Add another reply…"
                      style={{ flex:1, padding:"6px 12px", border:`1px solid ${borderColor}`, borderRadius:"4px", fontSize:fs(12), color:inkSoftColor }}
                      onKeyDown={e => e.key === "Enter" && handleAnswer(qa.id)}
                    />
                    <button onClick={() => handleAnswer(qa.id)} disabled={submittingA[qa.id] || !(replyTexts[qa.id] || "").trim()}
                      style={{ padding:"6px 12px", fontSize:fs(12), fontWeight:600, background:"transparent", border:`1px solid ${borderColor}`, borderRadius:"4px", cursor:"pointer", color:inkSoftColor, opacity: submittingA[qa.id] ? 0.5 : 1 }}>
                      {submittingA[qa.id] ? "…" : "Reply"}
                    </button>
                  </div>
                )}
              </div>
            ))
          }
        </div>
        </>
        )}

        {/* Recommendations */}
        <ProductRecommendations
          currentProductId={productId}
          currentStoreId={product.user_id}
          currentCategory={product.category}
        />

      </div>
    </div>
  );
}
